import { useCore } from '@/composables/core'
import type { Store } from 'vuex'
import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useLogger, useReadyTrigger } from '@booksy-com/vue-tools'
import type { RootState } from '@/types/store'
import type { ChatOpenOptions, ChatProvider, ChatProviderId } from '@/composables/chat/types'
import {
  DEFAULT_CHAT_PROVIDER_ID,
  setupChatProvider,
} from '@/composables/chat/providers/registry'

export type {
  ChatOpenOptions,
  ChatProvider,
  ChatProviderId,
  ChatBootstrapPayload,
} from '@/composables/chat/types'
export { isAgentforceChatBootstrap } from '@/composables/chat/types'
export { DEFAULT_CHAT_PROVIDER_ID } from '@/composables/chat/providers/registry'

type ChatProviderAction = 'open' | 'close' | 'toggle' | 'logout'

export interface UseChatResult {
  open: (options?: ChatOpenOptions) => Promise<void>
  close: () => Promise<void>
  toggle: () => Promise<void>
}

let chatProvider: ChatProvider | null = null

const logger = useLogger('chat')

type ChatReadyTrigger = ReturnType<typeof useReadyTrigger> & {
  isResolved?: boolean
}

let readyTrigger: ChatReadyTrigger | null = null
let shouldOpenWhenReady = false

const _logUninitializedError = (e?: unknown): void => {
  logger.error('chat provider not initialized')
  if (e) {
    logger.error(e)
  }
}

const _handleChatAction = (
  method: ChatProviderAction,
  options?: ChatOpenOptions
): Promise<void> => {
  if (!readyTrigger) {
    _logUninitializedError()
    return Promise.resolve()
  }

  return readyTrigger.promise
    .then(() => {
      const fn = chatProvider?.[method]
      if (fn) {
        ;(fn as (opts?: ChatOpenOptions) => void)(options)
      } else {
        _logUninitializedError()
      }
    })
    .catch((e: unknown) => {
      _logUninitializedError(e)
    })
}

const open = async (options?: ChatOpenOptions): Promise<void> =>
  _handleChatAction('open', options)
const close = async (): Promise<void> => _handleChatAction('close')
const toggle = async (): Promise<void> => _handleChatAction('toggle')

export type CreateChatOptions = {
  /** Which vendor integration to bootstrap. Defaults to {@link DEFAULT_CHAT_PROVIDER_ID}. */
  provider?: ChatProviderId
}

export const createChat = async (options?: CreateChatOptions): Promise<void> => {
  readyTrigger = useReadyTrigger()

  const { apiCall, store } = useCore()
  const business: ComputedRef<RootState['business']> = computed(
    () => (store as Store<RootState>).state.business
  )

  const setupChat = async (): Promise<void> => {
    if (!business.value.business?.id) {
      return
    }

    const providerId = options?.provider ?? DEFAULT_CHAT_PROVIDER_ID

    try {
      chatProvider = await setupChatProvider(providerId, {
        apiCall,
        businessId: business.value.business.id,
      })
      const trigger = readyTrigger
      if (trigger) trigger.resolveTrigger()

      // If support was requested before chat was ready, open it now
      if (shouldOpenWhenReady) {
        shouldOpenWhenReady = false
        void open()
      }
    } catch (e) {
      logger.error(e)
      const trigger = readyTrigger
      if (trigger) trigger.rejectTrigger()
    }
  }

  await setupChat()
}

export const destroyChat = async (): Promise<void> => {
  return _handleChatAction('logout').then(() => {
    chatProvider = null
    readyTrigger = null
    shouldOpenWhenReady = false
  })
}

export const openSupportWhenPossible = (): void => {
  if (readyTrigger?.isResolved === true) {
    // Chat is already ready, open immediately
    shouldOpenWhenReady = false
    void open()
  } else {
    // Remember to open when chat becomes ready
    shouldOpenWhenReady = true
  }
}

export const useChat = (): UseChatResult => ({
  open,
  close,
  toggle,
})
